import _ from "lodash";
// third party libraries
import update from "immutability-helper";
import html2canvas from "html2canvas";
import moment from "moment";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";

// Utils
import {
  getRubricDescriptorLabel,
  getFormattedResponse,
  getInputForAIResponse,
  checkIsDPAssessmentRubric,
  getMYPObjectivesData,
  convertMarkdownToHTML,
  getTableDataForMYPRubric,
  getContentToSend,
  getFormattedAssessmentFields,
  getParamsForAddToLEAction,
  getToolTypeFromContent,
  convertAIResponseToToolStructure,
  checkIsThinkingState,
  getIsLessonPlanningAIRevampEnabled,
  getUserMessageMetadata,
} from "./Utils";
import { generateRandomId, generateRandomNumber } from "Utils";
import { getFieldValue } from "Assessments/modules/Utils";
import {
  getIsAITutorEnhancementsV2Enabled,
  getIsAITutorModalitiesEnabled,
} from "Utils/ACLStoreUtils";
import {
  getAITutorModalitiesToolCalls,
  getAITutorModalityAttachmentPattern,
  getUnprocessedLinkAttachments,
  checkIfIntendedModalityCreated,
  AI_TUTOR_TOOL_NAME_STATE_MAPPING,
} from "Utils/AIUtils";

// Modules
import { getPlannerFieldMutationParams } from "UnitPlans/modules/UnitPlanModule";
import {
  getAssessmentToolNodeDetailsFromCacheV2,
  getUserBasicDetailsFromCache,
} from "modules/CommonGraphqlHelpers";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import {
  updateAssessmentInCache,
  updateAIDataInRedux,
} from "Assessments/modules/AssessmentModule";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { bulkUpdateRubricDescriptors } from "Rubric/modules/RubricModule";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { updateUnitPlanInCache } from "IBPlanner/modules/IBPlannerModule";
import {
  bulkEditMypObjectiveRubricDescriptor,
  updateAnalysisToolDetails,
  syncStandardsWithStandardBasedRubric,
  getAssessmentToolNodeDetails,
} from "modules/AssessmentTool/AssessmentToolModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "./LessonPlanningGraphqlHelpers";
import {
  createAIConversationMutation,
  createAIMessageMutation,
  createBulkAiMessagesMutation,
  updateAIMessageMutation,
} from "./LessonPlanningMutations";
import {
  getAIConversationsQuery,
  getAttachmentGroupAttachmentsQuery,
  getAssessmentBasicFieldsQuery,
} from "./LessonPlanningQueries";

// Constants and services
import {
  RUBRIC,
  CHECKLIST,
  SINGLE_POINT_RUBRIC,
  ASSESSMENT,
  WRITING,
  STREAMING,
  BROWSING_THE_INTERNET,
  THINKING,
  ELEMENT_TYPE_UNIT_PLAN,
  DP_ASSESSMENT_RUBRIC,
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  ANALYSIS,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  FETCHING_TRANSCRIPT,
} from "Constants/stringConstants";
import { ATTACHMENT_PICK_KEYS } from "./Constants";
import { TEMP_ID } from "AppComponents/AIContextBar/modules/Constants";
import {
  getAIResponse,
  uploadFileV2,
  updateAIConversation,
} from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import { ASSESSMENT_TOOLS_OBJ } from "@toddle-edu/common-utils";
import { STANDARD_UIDS } from "modules/AssessmentTool/Constants";
import { getGroupedFields } from "../components/PrerequisiteSelection/Utils";
import { writeAnalysisElementInCache } from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";
import {
  bulkCreateStandardRubricCriteria,
  removeStandardRubricCriterias,
  updateStandardRubricDescriptors,
} from "AppComponents/StandardRubric/modules/StandardRubricModule";

export let cancelRequest;

export const createAIMessage = ({
  variables,
  writeToCache = true,
  isAITutor = false,
  isRevampUIEnabled = false,
}) => {
  const userInfo = getUserInfo();
  const attachmentsForCacheWrite = _.map(
    _.get(variables, "input.attachments"),
    att => ({
      id: generateRandomId(),
      modalityAttachment: null,
      ...att,
      signedUrl: null,
      streamUrl: null,
      createdAt: null,
      metadata: null,
      __typename: "Attachment",
    })
  );

  // Remove modalityAttachment from attachments for mutation variables
  const attachmentsForMutation = _.map(
    _.get(variables, "input.attachments"),
    att => _.omit(att, ["modalityAttachment", "originalAttachmentId"])
  );

  const variablesForMutation = update(variables, {
    input: {
      attachments: {
        $set: attachmentsForMutation,
      },
    },
  });

  return async dispatch => {
    const newCreateAiMessage = {
      isSuccess: true,
      response: {
        id: generateRandomId(),
        role: null,
        authorType: _.get(variables, "input.authorType"),
        content: _.get(variables, "input.content"),
        metadata: _.get(variables, "input.metadata"),
        attachments: attachmentsForCacheWrite,
        createdBy: {
          ...getUserBasicDetailsFromCache({
            id: userInfo.id,
            type: userInfo.userEntityType,
          }),
          type: userInfo.user_type,
          schoolTenures: [],
        },
        votes: {
          myVote: null,
          __typename: "VoteConnection",
        },
        usageMetricsId: null,
        parentMessage: {
          id: null,
          __typename: "AiMessage",
        },
        __typename: "AiMessage",
      },
      __typename: "GenericAiMessageResponse",
    };

    try {
      return await client.mutate({
        mutation: createAIMessageMutation,
        variables: variablesForMutation,
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            createAiMessage: newCreateAiMessage,
          },
        },
        update: (
          cache,
          {
            data: {
              ai: { createAiMessage },
            },
          }
        ) => {
          if (writeToCache) {
            const createAiMessageNode = {
              node: _.get(createAiMessage, "response"),
              __typename: "AiMessageEdge",
            };

            const conversation = getAIConversationsFromCache({
              id: _.get(variables, "input.conversationId"),
            });

            let dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: [createAiMessageNode],
                },
                totalCount: {
                  $set: _.get(conversation, "messages.totalCount", 0) + 1,
                },
              },
            });

            // Clear conversation attachments when AI Tutor revamp UI is enabled
            // as they are now stored in the message attachments
            if (isAITutor && isRevampUIEnabled) {
              dataToSend = update(dataToSend, {
                attachments: {
                  $set: [],
                },
              });
            }

            writeAIConversationsInCache({
              variables: {
                id: _.get(variables, "input.conversationId"),
              },
              data: {
                node: dataToSend,
              },
            });
          }
        },
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const createBulkAiMessages = input => {
  return async dispatch => {
    const { conversationId, messages } = input;

    const conversation = getAIConversationsFromCache({
      id: conversationId,
    });

    const userInfo = getUserInfo();
    const createdBy = {
      ...getUserBasicDetailsFromCache({
        id: userInfo.id,
        type: userInfo.userEntityType,
      }),
      type: userInfo.user_type,
      schoolTenures: [],
    };

    // Create optimistic responses for all messages
    const optimisticMessages = _.map(messages, message => ({
      id: generateRandomId(),
      role: null,
      severity: null,
      authorType: _.get(message, "authorType"),
      content: _.get(message, "content"),
      metadata: _.get(message, "metadata", {}),
      attachments: _.get(message, "attachments", null),
      createdBy,
      votes: {
        myVote: null,
        __typename: "VoteConnection",
      },
      createdAt: moment().toISOString(),
      usageMetricsId: _.get(message, "usageMetricsId", null),
      parentMessage: {
        id: _.get(message, "parentMessageId", null),
        __typename: "AiMessage",
      },
      __typename: "AiMessage",
    }));

    try {
      const result = await client.mutate({
        mutation: createBulkAiMessagesMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            createBulkAiMessages: {
              isSuccess: true,
              response: optimisticMessages,
              __typename: "BulkAiMessagesResponse",
            },
          },
        },
        update: (
          cache,
          {
            data: {
              ai: { createBulkAiMessages },
            },
          }
        ) => {
          cache.updateQuery(
            {
              query: getAIConversationsQuery,
              variables: {
                id: conversationId,
              },
            },
            data => {
              if (!data) return data;

              const nodes = _.map(createBulkAiMessages?.response, node => {
                return { node, __typename: "AiMessageEdge" };
              }).reverse();

              if (!_.isEmpty(nodes)) {
                // Filter out existing edges where node.id starts with "NEW_"
                const existingEdges = _.get(data, "node.messages.edges", []);
                const filteredEdges = _.filter(existingEdges, edge => {
                  const messageId = _.get(edge, "node.id", "");

                  return (
                    !_.startsWith(messageId, "NEW_") &&
                    !_.includes(messageId, "-") // ${conversationId}-${id} case in generateProficiencyLevelConversation
                  );
                });

                const updatedData = update(data, {
                  node: {
                    messages: {
                      edges: {
                        $set: [...nodes, ...filteredEdges],
                      },
                      totalCount: {
                        $set: filteredEdges.length + _.size(nodes),
                      },
                    },
                  },
                });

                return updatedData;
              }

              return data;
            }
          );
        },
      });

      return _.get(result, "data.ai.createBulkAiMessages.response", []);
    } catch (err) {
      // Revert cache on error
      cache.updateQuery(
        {
          query: getAIConversationsQuery,
          variables: {
            id: conversationId,
          },
        },
        () => ({ node: conversation })
      );

      if (err) dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

const updateAssessmentFieldLocal = ({
  t,
  params,
  extraParams = {},
  field_list = {},
  portalType,
  resourceFields,
  isDebounce = true,
  isContextSelection = false,
}) => {
  const fields = [];

  return async dispatch => {
    _.map(params, (obj, key) => {
      const fieldObj = _.find(resourceFields, { uid: key }) || {};

      const isDescriptionUpdateFromAIInput =
        _.get(fieldObj, "uid") === "description";

      if (_.isEmpty(fieldObj)) return;

      const fieldTemplateObj = field_list?.[key];
      const { id, value: oldValue } = fieldObj;
      const value = { id, value: obj };

      // if same value is being set then no need to update the cache and make a call to the backend
      // edge case : for measureAssessing we store assessment tool type, so it'll be the same even if tool content is being updated so skip the check
      if (isContextSelection && _.isEqual(obj, oldValue)) {
        return;
      }

      dispatch(
        updateAssessmentInCache({
          fieldUID: key,
          params: { ...value, uid: key },
          fieldObj,
          fieldTemplateObj,
          extraParams,
        })
      );

      const mutationParams = getPlannerFieldMutationParams({
        id,
        oldValue,
        hasAddRemoveValues: false,
        newValue: obj,
      });

      fields.push({ ...mutationParams, isDescriptionUpdateFromAIInput });
    });

    if (!_.isEmpty(fields)) {
      dispatch(
        updateField({
          key: generateRandomId(),
          params: {
            fields,
            portalType,
          },
          type: ASSESSMENT,
          isDebounce,
          t,
        })
      );
    }
  };
};

export const updateAssignmentField = params => {
  const {
    key,
    content: _content,
    assessmentDetails,
    selectedAssessmentField,
    metadata,
    refetchAssessments,
  } = params;

  const content = (_content || "")
    .replace(/<thinking>[\s\S]*?<\/thinking>/g, "")
    .replace(/<output>([\s\S]*?)<\/output>/g, "$1")
    .trim();

  return async (dispatch, getState) => {
    const state = getState();
    const curriculumProgramType = getCurrentCurriculumProgramType(state);

    const resourceFields = _.get(assessmentDetails, "allFields", []);
    const field = selectedAssessmentField;

    if (ASSESSMENT_TOOLS_OBJ?.[field] && key !== "add_to_le") {
      const assessmentTools = _.get(
        assessmentDetails,
        "assessmentTools.edges",
        []
      );

      const selectedAssessmentTool = _.find(assessmentTools, ({ node }) => {
        return _.get(node, "assessmentToolType") === field;
      });

      const assessmentToolItem = _.get(
        selectedAssessmentTool,
        "node.assessmentToolItem",
        {}
      );

      const selectedAssessmentToolIndex = _.findIndex(
        assessmentTools,
        ({ node }) => {
          return _.get(node, "assessmentToolType") === field;
        }
      );

      const insertAt =
        selectedAssessmentToolIndex === -1 ? null : selectedAssessmentToolIndex;

      const assessmentToolInput = {
        parentId: _.get(assessmentDetails, "id"),
        assessmentId: _.get(assessmentDetails, "id"),
        parentType: ASSESSMENT,
        id: _.get(selectedAssessmentTool, "node.assessmentToolItem.id"),
        type: field,
        isGlobal: false,
        portalType: "PLANNER",
      };

      switch (field) {
        case RUBRIC:
          {
            const {
              criterias = [],
              indicators = [],
              descriptors = [],
            } = convertAIResponseToToolStructure({
              metadata: { fieldType: selectedAssessmentField },
              content: await getFormattedResponse({
                content: content,
                fieldType: selectedAssessmentField,
              }),
            });

            const id = await dispatch(
              createAssessmentTool({
                ...assessmentToolInput,
                assessmentId: _.get(assessmentDetails, "id"),
                extraParams: {
                  criterias: _.map(criterias, criteria =>
                    _.get(criteria, "label")
                  ),
                  indicators: _.map(indicators, indicator =>
                    _.pick(indicator, ["label"])
                  ),
                  insertAt,
                  removeToolId: _.get(assessmentToolInput, "id"),
                },
              })
            );

            const rubric = getAssessmentToolNodeDetailsFromCacheV2({
              id,
              type: field,
            });

            dispatch(
              bulkUpdateRubricDescriptors({
                descriptors: _.map(rubric?.descriptors, descriptor => {
                  const label = getRubricDescriptorLabel({
                    criterias,
                    indicators,
                    descriptors,
                    descriptor,
                  });

                  return {
                    id: _.get(descriptor, "id"),
                    label,
                  };
                }),
              })
            );
          }
          break;
        case CHECKLIST:
          {
            const {
              checklistOptionItems = [],
              checklistItems = [],
            } = convertAIResponseToToolStructure({
              metadata: { fieldType: selectedAssessmentField },
              content: await getFormattedResponse({
                content: content,
                fieldType: selectedAssessmentField,
              }),
            });

            await dispatch(
              createAssessmentTool({
                ...assessmentToolInput,
                assessmentId: _.get(assessmentDetails, "id"),
                extraParams: {
                  checklistOptionItems: _.map(checklistOptionItems, item =>
                    _.pick(item, "label")
                  ),
                  checklistItems: _.map(checklistItems, item =>
                    _.pick(item, ["label"])
                  ),
                  insertAt,
                  removeToolId: _.get(assessmentToolInput, "id"),
                },
              })
            );
          }
          break;
        case SINGLE_POINT_RUBRIC: {
          const {
            criterias = [],
            standards = [],
          } = convertAIResponseToToolStructure({
            metadata: { fieldType: selectedAssessmentField },
            content: await getFormattedResponse({
              content: content,
              fieldType: selectedAssessmentField,
            }),
          });

          await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              assessmentId: _.get(assessmentDetails, "id"),
              extraParams: {
                criterias,
                standards,
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );
          break;
        }
        case DP_ASSESSMENT_RUBRIC: {
          const {
            metadata: { sourceDpRubricId, sourceDpRubricObjectiveIds } = {},
          } = metadata || {};

          if (!checkIsDPAssessmentRubric({ metadata })) return;

          const assessmentToolData = [
            {
              parentId: assessmentToolInput.parentId,
              parentType: assessmentToolInput.parentType,
              toolType: field,
              sourceRubricId: sourceDpRubricId,
              addedObjectiveIds: sourceDpRubricObjectiveIds,
            },
          ];

          await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              assessmentToolData,
              extraParams: {
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );
          break;
        }
        case MYP_OBJECTIVE_RUBRIC:
        case MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC: {
          // 1. get objective ids for the AI generated rubric
          const { objectiveIds, updateObjectives } = getMYPObjectivesData({
            field,
            content,
            assessmentDetails,
            metadata,
          });

          // 2. store the objective Ids
          if (!_.isEmpty(objectiveIds) && updateObjectives) {
            await dispatch(
              updateAssessmentFieldLocal({
                ...params,
                params: {
                  [`${ASSESSMENT_TOOLS_OBJ[field]?.objectiveFieldKey}`]: objectiveIds,
                },
                resourceFields,
              })
            );
          }

          // 3. create the assessment tool
          const id = await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              assessmentId: _.get(assessmentDetails, "id"),
              assessmentToolData: {
                objectiveIds,
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );

          if (id) {
            const tool = getAssessmentToolNodeDetailsFromCacheV2({
              id,
              type: field,
            });

            // 4. get the descriptors from the AI generated rubric table
            const descriptors = convertAIResponseToToolStructure({
              metadata: {
                ...(metadata || {}),
                fieldType: selectedAssessmentField,
              },
              content: getFormattedResponse({
                content: content,
                fieldType: selectedAssessmentField,
                assessmentDetails,
                metadata,
              }),
              tool,
              assessmentDetails,
            });

            // 5. update the descriptors of the rubric
            if (!_.isEmpty(descriptors)) {
              await dispatch(bulkEditMypObjectiveRubricDescriptor(descriptors));
            }
          }

          if (refetchAssessments) {
            await refetchAssessments?.();
          }

          break;
        }
        case ANALYSIS: {
          const { criterias, description } = convertAIResponseToToolStructure({
            metadata: { fieldType: selectedAssessmentField },
            content: await getFormattedResponse({
              content: content,
              fieldType: selectedAssessmentField,
            }),
          });

          const _updateAnalysisToolDetails = _.map(
            assessmentToolItem?.criterias,
            (criterion, index) => {
              const { id } = criterion || {};
              writeAnalysisElementInCache({
                id,
                type: "CRITERIA",
                data: {
                  ...criterion,
                  label: criterias?.[index],
                  description: description?.[index],
                },
              });

              return {
                id,
                type: "CRITERIA",
                label: criterias?.[index],
                description: description?.[index],
              };
            }
          );

          await dispatch(
            updateAnalysisToolDetails({
              input: { updateAnalysisToolDetails: _updateAnalysisToolDetails },
            })
          );

          break;
        }
        case SCORE_BASED_RUBRIC: {
          // 1. extract data from the AI generated content
          const response = convertAIResponseToToolStructure({
            metadata: { fieldType: selectedAssessmentField },
            content: await getFormattedResponse({
              content: content,
              fieldType: selectedAssessmentField,
            }),
          });

          // 2. create score based rubric tool
          const toolId = await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              assessmentId: _.get(assessmentDetails, "id"),
              extraParams: {
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );

          // 3. if tool is created with default criteria then remove them
          const scoreBasedRubric = getAssessmentToolNodeDetailsFromCacheV2({
            id: toolId,
            type: field,
          });

          const existingCriteriaIds = _.chain(scoreBasedRubric)
            .get("standardRubricCriterias", [])
            .map("id")
            .compact()
            .value();

          if (!_.isEmpty(existingCriteriaIds)) {
            await dispatch(
              removeStandardRubricCriterias({
                toolId,
                ids: existingCriteriaIds,
                type: field,
              })
            );
          }

          // 4. create criteria from the AI generated content
          const input = _.map(response, (item, index) => {
            const { label, description, ratings } = item || {};

            const descriptors = _.map(ratings, (rating, index) => {
              const {
                label: ratingLabel,
                description: descriptionLabel,
                score,
              } = rating || {};

              return {
                label: ratingLabel,
                description: descriptionLabel || null,
                score,
                displaySequence: index + 1,
              };
            });

            return {
              toolId,
              label,
              description: description || null,
              displaySequence: index + 1,
              descriptors,
            };
          });

          await dispatch(
            bulkCreateStandardRubricCriteria({
              input,
              type: field,
              skipPutAfterItemId: true,
            })
          );

          break;
        }

        case STANDARD_BASED_RUBRIC: {
          const { metadata: { elementIds, gradeScaleId, elementType } = {} } =
            metadata || {};

          // 1. extract data from the AI generated content
          const response = convertAIResponseToToolStructure({
            metadata: { fieldType: selectedAssessmentField },
            content: await getFormattedResponse({
              content: content,
              fieldType: selectedAssessmentField,
            }),
          });

          // 2. create standard based rubric tool
          const toolId = await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              assessmentId: _.get(assessmentDetails, "id"),
              extraParams: {
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
                gradeScaleId,
              },
            })
          );

          // 2. create criteria (rows)
          const criteriaInput = _.map(elementIds, (id, index) => ({
            elementId: id,
            gradeScaleId,
            toolId,
            displaySequence: index + 1,
            elementType,
          }));

          const criteria = await dispatch(
            bulkCreateStandardRubricCriteria({
              input: criteriaInput,
              type: field,
              shouldFetchToolNodeDetails: false, // called below after descriptors update
            })
          );

          // 4. refetch tool details after creating criteria
          await dispatch(
            getAssessmentToolNodeDetails({
              id: toolId,
              type: "STANDARD_RUBRIC",
            })
          );

          // 5. update descriptors (columns) from the AI generated content
          const descriptorsInput = _.reduce(
            criteria,
            (acc, criterion, index) => {
              const descriptors = _.get(criterion, "descriptors", []);
              const responseDescriptors = _.get(
                response,
                `${index}.ratings`,
                []
              );

              _.forEach(descriptors, (descriptor, index) => {
                const { id } = descriptor || {};
                const description = _.get(
                  responseDescriptors,
                  `[${index}].description`,
                  null
                );

                acc.push({ id, description });
              });

              return acc;
            },
            []
          );

          await dispatch(
            updateStandardRubricDescriptors({
              input: descriptorsInput,
              toolId,
              type: field,
            })
          );

          // 6. sync standard learning goals with standard based rubric
          const standardUID = STANDARD_UIDS[curriculumProgramType];
          const standardField = _.find(resourceFields, { uid: standardUID });
          const initialSelectedStandards = _.get(standardField, "value", []);

          await dispatch(
            syncStandardsWithStandardBasedRubric({
              assessmentId: assessmentToolInput.assessmentId,
              initialSelectedStandards, // already selected standards in the learning goals section
              selectedStandards: elementIds, // standards in the rubric,
              isTemplate: true, // this makes sure that we never delete any tagged standard
            })
          );

          break;
        }
      }

      dispatch(
        updateAssessmentFieldLocal({
          ...params,
          params: { measureAssessing: selectedAssessmentField },
          resourceFields,
        })
      );
    } else {
      dispatch(updateAssessmentFieldLocal({ ...params, resourceFields }));
    }
  };
};

export const getAssessmentFieldsByUIDs = async ({ assessmentId, uids }) => {
  try {
    const response = await client.query({
      query: getAssessmentBasicFieldsQuery,
      variables: {
        id: assessmentId,
        uids,
      },
      fetchPolicy: "network-only",
    });

    return _.get(response, "data.node", {});
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getAttachmentGroupAttachments = async attachmentGroupId => {
  try {
    const response = await client.query({
      query: getAttachmentGroupAttachmentsQuery,
      variables: {
        id: attachmentGroupId,
      },
      fetchPolicy: "network-only",
    });

    return _.get(response, "data.node", {});
  } catch (error) {
    console.error(error);
    return null;
  }
};

const processToolResponse = async params => {
  const { parsedMessages, assessmentDetails } = params;

  const result = {};
  const uidsToRefetch = new Set();

  const toolResponses = _.map(
    _.filter(parsedMessages, item => item.message?.role === "tool"),
    "message.toolResponse"
  );

  if (!_.isEmpty(toolResponses)) {
    for (const toolResponse of toolResponses) {
      try {
        const parsedToolResponse = JSON.parse(toolResponse || null);
        const { isSuccess, response } = parsedToolResponse || {};

        if (!isSuccess) continue;

        if (_.has(response, "fieldUids")) {
          _.forEach(response.fieldUids, uid => uidsToRefetch.add(uid));
          uidsToRefetch.add("openingMessageResource");
          result.isDraftTaskMessage = !getIsAITutorModalitiesEnabled();
        } else if (_.has(response, "attachmentGroupId")) {
          const attachmentGroup = await getAttachmentGroupAttachments(
            response.attachmentGroupId
          );

          let attachments = _.get(attachmentGroup, "attachments");

          attachments = _.map(attachments, attachment => {
            const base = _.omit(attachment, ["id", "__typename"]);

            const toddleDoc = _.isEmpty(attachment.toddleDoc?.value)
              ? undefined
              : _.omit(attachment.toddleDoc, "__typename");

            const modalityFile = _.isEmpty(attachment.modalityFile?.content)
              ? undefined
              : _.omit(attachment.modalityFile, "__typename");

            return {
              ...base,
              toddleDoc,
              modalityFile,
              createdBy: getUserInfo().id,
              presetAttachment: _.get(attachment, "id", null),
            };
          });

          result.attachments = attachments;
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  if (uidsToRefetch.size > 0) {
    await getAssessmentFieldsByUIDs({
      assessmentId: _.get(assessmentDetails, "id"),
      uids: Array.from(uidsToRefetch),
    });
  }

  return result;
};

export const onSendQuery = params => {
  const {
    t,
    query,
    leId,
    aiConversationId,
    selectedAction,
    learningTool,
    setSelectedAction,
    setCurrentAIState,
    gradeId,
    course,
    language,
    context = {},
    fieldList,
    unitPlanTemplate,
    unitPlanDetails,
    messages,
    assessmentDetails,
    excludedFields,
    entityType,
    stepList,
    template,
    setContext,
    promptFields = [],
    prompts,
    attachments, // local state
    conversationAttachments,
    isAITutor,
    mode,
    setSendButtonParams = _.noop,
    sendButtonParams = {},
    learningCourseId,
    schoolInstructions,
    toolChoice,
    toolNames,
    selectedLearningTool,
    setSelectedLearningTool,
    assistantMetadata = {},
  } = params;

  const isRevampUIEnabled = isAITutor
    ? getIsAITutorModalitiesEnabled()
    : getIsLessonPlanningAIRevampEnabled();

  const selectedActionClone = _.cloneDeep(selectedAction);
  const promptMetadata = _.get(selectedActionClone, "metadata", {});

  const { field_list: unitPlanFieldList, step_list: unitPlanStepList } =
    unitPlanTemplate || {};

  return async (dispatch, getState) => {
    try {
      const isEnglishLanguageSelected = language === "en";
      let shouldProcessAttachments = !_.isEmpty(conversationAttachments);

      if (isRevampUIEnabled) {
        shouldProcessAttachments = !_.isEmpty(attachments);
      }

      // 1. prepare the content to send
      const { content, userInstructions = "" } = getContentToSend({
        t,
        selectedAction,
        isEnglishLanguageSelected,
        query,
        isAITutor,
        conversationAttachments,
      });

      if (_.isEmpty(content) && _.size(attachments) === 0) return;

      // 2. show writing... animation
      setCurrentAIState(WRITING);

      const userMessageMetadata = {
        isUserInputMessage: _.isEmpty(selectedAction),
        ...(!_.isEmpty(learningTool) && { learningTool }),
        ...getUserMessageMetadata({
          selectedAction,
          selectedLearningTool,
          setSelectedLearningTool,
        }),
      };

      const updatedAttachments = _.reduce(
        attachments,
        (acc, attachment) => {
          let result = {
            mimeType: null,
            thumbUrl: null,
            title: null,
            createdBy: null,
            url: null,
            parentType: "AI_MESSAGE",
            presetAttachment: null,
            ..._.pick(attachment, ATTACHMENT_PICK_KEYS),
          };

          if (_.startsWith(attachment?.id, "NEW_")) {
            const originalAttachmentId = attachment?.originalAttachmentId;
            if (
              originalAttachmentId &&
              !_.startsWith(originalAttachmentId, "NEW_")
            ) {
              _.set(result, "id", attachment?.originalAttachmentId);
            } else {
              result = _.omit(result, ["id"]);
            }
          }

          acc.push(result);
          return acc;
        },
        []
      );

      const allMessagesImageUrls = _.compact(
        _.flatMap(messages, ({ node }) => {
          const { metadata } = node || {};
          return _.get(metadata, "imageUrls", []);
        })
      );

      let imageUrls;
      let audioUrls;

      if (isRevampUIEnabled) {
        imageUrls = _.map(
          _.filter(updatedAttachments, ({ type }) => type === "IMAGE"),
          "url"
        );

        audioUrls = _.map(
          _.filter(updatedAttachments, ({ type }) => type === "AUDIO"),
          "url"
        );
      } else {
        imageUrls = _.map(
          _.filter(conversationAttachments, attachment => {
            const { url, type } = attachment || {};
            return type === "IMAGE" && !_.includes(allMessagesImageUrls, url);
          }),
          "url"
        );
      }

      const shouldStoreImageUrl =
        _.isEmpty(messages) ||
        !_.some(messages, ({ node }) => {
          const { metadata } = node || {};

          return _.isEmpty(_.xor(imageUrls, metadata?.imageUrls));
        });

      if (shouldStoreImageUrl && !_.isEmpty(imageUrls)) {
        _.set(userMessageMetadata, "imageUrls", imageUrls);
      }

      if (!_.isEmpty(audioUrls)) {
        _.set(userMessageMetadata, "audioUrls", audioUrls);
      }

      let userMessageResponseId = _.get(sendButtonParams, "userMessageId");

      // 3. create user message if required
      if (
        _.get(selectedAction, "createUserMessage", true) &&
        _.get(sendButtonParams, "createUserMessage", true)
      ) {
        const lastMessageId = _.get(_.last(messages), "node.id");

        const messageResponse = await dispatch(
          createAIMessage({
            variables: {
              input: {
                conversationId: aiConversationId,
                authorType: "user",
                content: _.trim(`${content} ${userInstructions}`),
                attachments: updatedAttachments,
                metadata: userMessageMetadata,
                parentMessageId: lastMessageId,
              },
            },
            isAITutor,
            isRevampUIEnabled,
          })
        );

        const response = _.get(
          messageResponse,
          "data.ai.createAiMessage.response",
          {}
        );

        userMessageResponseId = _.get(response, "id", null);

        const unprocessedLinks = getUnprocessedLinkAttachments([
          { node: response },
        ]);

        const isLinkTypeWithProcessing =
          isRevampUIEnabled && !_.isEmpty(unprocessedLinks);

        if (isLinkTypeWithProcessing) {
          setCurrentAIState(FETCHING_TRANSCRIPT);
          return;
        }
      }

      const hasTempAttachment = _.some(conversationAttachments, ({ id }) =>
        _.includes(id, TEMP_ID)
      );

      if (hasTempAttachment) {
        setSendButtonParams(prev => ({
          ...prev,
          content,
          attachmentHasTempId: true,
          checkLoadingState: false,
          createUserMessage: false,
        }));

        setCurrentAIState("");
        return;
      }

      // 3. generate AI response
      const conversation = getAIConversationsFromCache({
        id: aiConversationId,
      });

      let parentMessageId = _.get(conversation, "messages.edges[0].node.id");

      const id = generateRandomId();
      let responseContent = {};

      const userInfo = getUserInfo();
      const createdBy = {
        ...getUserBasicDetailsFromCache({
          id: userInfo.id,
          type: userInfo.userEntityType,
        }),
        type: userInfo.user_type,
        schoolTenures: [],
      };

      const savedToolUseIds = [],
        messagesInput = [];

      const isAITutorEnhancementsV2Enabled = getIsAITutorEnhancementsV2Enabled();
      const modalitiesToolCalls = getAITutorModalitiesToolCalls();
      const modalityAttachmentPattern = getAITutorModalityAttachmentPattern();

      const response = await dispatch(
        getAIResponse({
          useStreamParserV2: isAITutorEnhancementsV2Enabled,
          variables: {
            input: getInputForAIResponse({
              t,
              leId,
              content: content ?? "",
              selectedAction,
              gradeId,
              entityType,
              course,
              curriculumProgramId: getSelectedCurriculumId(getState()),
              curriculumProgramType: getCurrentCurriculumProgramType(
                getState()
              ),
              context,
              assessmentDetails,
              unitPlanFields: _.get(unitPlanDetails, "unitPlanFields"),
              unitPlanTemplate,
              excludedFields,
              template,
              userInstructions,
              prompts,
              shouldProcessAttachments,
              imageUrls: shouldStoreImageUrl ? imageUrls : [],
              ...(isRevampUIEnabled && {
                imageUrls,
                audioUrls,
              }),
              aiConversationId,
              isAITutor,
              learningCourseId,
              schoolInstructions,
              userMessageResponseId,
              isRevampUIEnabled,
              ...(toolChoice && { toolChoice }),
              ...(toolNames && { toolNames }),
              learningTool,
            }),
          },
          getCancelToken: token => {
            cancelRequest = token;
          },
          onDownloadProgress: params => {
            const {
              action,
              fieldType,
              content,
              usageMetricsId,
              userInputFlagged,
              model,
              temperature,
              intentUsageMetricsId,
              prerequisitesFields,
              skipGPTCall = false,
              metadata,
              languageCode,
              tools,
              toolCalls,
              parsedMessages,
              latestChunkStatus,
            } = params || {};

            if (!_.isEmpty(prerequisitesFields)) {
              const groupedFields = getGroupedFields({
                t,
                fields: prerequisitesFields,
                fieldList,
                unitPlanFieldList,
                unitPlanDetails,
                assessmentDetails,
                stepList,
                unitPlanStepList,
              });

              dispatch(
                createAIMessage({
                  variables: {
                    input: {
                      conversationId: aiConversationId,
                      authorType: "prerequisite",
                      content,
                      attachments: null,
                      metadata: {
                        fields: groupedFields,
                        fieldType,
                        action,
                        skipGPTCall,
                        metadata,
                        language: languageCode || language,
                      },
                    },
                  },
                })
              );

              return;
            }

            const shouldCreateToolMessages = isAITutor
              ? isAITutorEnhancementsV2Enabled
              : isRevampUIEnabled;

            if (shouldCreateToolMessages) {
              if (!_.isEmpty(parsedMessages)) {
                const toolUseIds = _.chain(parsedMessages)
                  .map("message.toolCallId")
                  .compact()
                  .value();

                if (!_.isEqual(toolUseIds, savedToolUseIds)) {
                  parentMessageId = userMessageResponseId;

                  const flatMessages = _.chain(parsedMessages)
                    .filter(item => _.has(item, "message"))
                    .map("message")
                    .value();

                  const newToolUseIds = _.difference(
                    toolUseIds,
                    savedToolUseIds
                  );

                  // iterate over each new tool call
                  _.forEach(newToolUseIds, toolUseId => {
                    // find the assistant message that triggered the tool call
                    const assistantToolCallMessage = _.find(
                      flatMessages,
                      item => {
                        const toolCallIds = _.map(item?.toolCalls, "id");
                        return _.includes(toolCallIds, toolUseId);
                      }
                    );

                    const { toolCalls } = assistantToolCallMessage || {};

                    const toolResponseParsedMessage = _.find(
                      flatMessages,
                      item => {
                        const { role, toolCallId } = item || {};
                        const isTool = role === "tool";
                        return isTool && toolCallId === toolUseId;
                      }
                    );

                    const toolCallId = toolResponseParsedMessage?.toolCallId;

                    if (!_.isEmpty(toolCallId)) {
                      messagesInput.push(
                        {
                          authorType: "assistant",
                          content: "",
                          usageMetricsId,
                          metadata: {
                            toolCalls: _.filter(toolCalls, {
                              id: toolCallId,
                            }),
                          },
                          parentMessageId,
                        },
                        {
                          authorType: "tool",
                          content: toolResponseParsedMessage?.toolResponse,
                          metadata: {
                            toolCallId: toolCallId,
                          },
                          parentMessageId,
                        }
                      );
                    }

                    // add toolUseId to savedToolUseIds
                    if (!_.includes(savedToolUseIds, toolUseId)) {
                      savedToolUseIds.push(toolUseId);
                    }
                  });
                }
              }
            }

            // when user cancels the request, responseContent will contain the response generated till that point
            responseContent = params;

            let _content = userInputFlagged
              ? t("common:user_input_flagged_msg")
              : content;

            // if modality creation tool call does not happen, remove modality attachment placeholder from the content
            const modalityToolCall = _.some(toolCalls, tool => {
              return _.includes(modalitiesToolCalls, tool?.function?.name);
            });

            if (!modalityToolCall) {
              _content = _.replace(
                _content,
                new RegExp(modalityAttachmentPattern, "g"),
                ""
              );
            }

            const createAiMessageNode = {
              node: {
                id,
                role: null,
                authorType: "assistant",
                content: _content,
                attachments: null,
                metadata: {
                  fieldType,
                  userInputFlagged,
                  model,
                  temperature,
                  intentUsageMetricsId,
                  metadata,
                  language: languageCode || language,
                },
                createdBy,
                votes: {
                  myVote: null,
                  __typename: "VoteConnection",
                },
                usageMetricsId: usageMetricsId || null,
                parentMessage: {
                  id: null,
                  __typename: "AiMessage",
                },
                __typename: "AiMessage",
              },
              __typename: "AiMessageEdge",
            };

            let nodesToUnshift = [createAiMessageNode];

            // Check if messagesInput has tool messages and create nodes for them
            const toolMessageNodes = [];
            const hasToolMessages = _.some(
              messagesInput,
              message => message.authorType === "tool"
            );

            if (hasToolMessages) {
              const toolMessages = _.filter(
                messagesInput,
                message => message?.authorType === "tool"
              );

              _.forEach(toolMessages, (toolMessage, index) => {
                const toolMessageNode = {
                  node: {
                    id: `id-${index}`,
                    role: null,
                    authorType: "tool",
                    content: toolMessage.content,
                    attachments: null,
                    metadata: toolMessage.metadata,
                    createdBy,
                    votes: {
                      myVote: null,
                      __typename: "VoteConnection",
                    },
                    usageMetricsId: null,
                    parentMessage: {
                      id: null,
                      __typename: "AiMessage",
                    },
                    __typename: "AiMessage",
                  },
                  __typename: "AiMessageEdge",
                };

                toolMessageNodes.push(toolMessageNode);
              });

              nodesToUnshift = [createAiMessageNode, ...toolMessageNodes];
            }

            const dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: nodesToUnshift,
                },
                totalCount: {
                  $set:
                    _.get(conversation, "messages.totalCount", 0) +
                    nodesToUnshift.length,
                },
              },
            });

            if (
              isAITutor &&
              isRevampUIEnabled &&
              latestChunkStatus === "TOOL_INDICATION"
            ) {
              const latestToolCall = _.chain(toolCalls)
                .last()
                .get("function.name")
                .value();

              const newState = _.get(
                AI_TUTOR_TOOL_NAME_STATE_MAPPING,
                latestToolCall,
                THINKING
              );

              setCurrentAIState(newState);
              return;
            }

            if (
              (!_.isEmpty(tools) || !_.isEmpty(toolCalls)) &&
              _.isEmpty(content)
            ) {
              const isSearchTool = _.some(toolCalls, tool =>
                ["search", "search_v2"].includes(tool?.function?.name)
              );

              if (isSearchTool) setCurrentAIState(BROWSING_THE_INTERNET);
              else setCurrentAIState(THINKING);
              return;
            }

            if (!_.isEmpty(_content)) {
              writeAIConversationsInCache({
                variables: {
                  id: aiConversationId,
                },
                data: {
                  node: dataToSend,
                },
              });

              if (
                (isRevampUIEnabled || !isAITutor) &&
                checkIsThinkingState([createAiMessageNode])
              ) {
                setCurrentAIState(THINKING);
              } else {
                setCurrentAIState(STREAMING);
              }
            }
          },
        })
      );

      // 4. hide "Stop generation" button and reset local states
      setCurrentAIState("");
      setSelectedAction({});
      setSendButtonParams({});

      // 6. reset the fields if shouldReset is true in the config so that it'll get asked each time
      if (!_.isEmpty(context)) {
        const lastPrerequisiteMessage = _.findLast(
          messages,
          ({ node } = {}) => {
            return node?.authorType === "prerequisite";
          }
        );

        const omitContextKeys = _.chain(lastPrerequisiteMessage)
          .get("node.metadata.fields", {})
          .flatMap(value =>
            _.map(value, item => {
              const { field, config } = item || {};
              return {
                field,
                shouldReset: _.get(config, "shouldReset", false),
              };
            })
          )
          .filter(item => item?.shouldReset)
          .map("field")
          .value();

        setContext(_.omit(context, omitContextKeys));
      }

      // 7. create a message of the generated response from AI
      const {
        content: aiResponse,
        fieldType,
        usageMetricsId,
        model,
        temperature,
        intentUsageMetricsId,
        userInputFlagged = false,
        metadata,
        languageCode,
        toolCalls,
        parsedMessages,
      } = response || responseContent || {};

      if (!_.isEmpty(aiResponse) || userInputFlagged) {
        const content = userInputFlagged
          ? t("common:user_input_flagged_msg")
          : aiResponse;

        let mypCriteriaMapping = {};

        const allFields = _.get(assessmentDetails, "allFields", []);

        const objectivesMYP = getFieldValue({
          fields: allFields,
          uid: "objectivesMYP",
        });

        const interdisciplinaryCriteriaMYP = getFieldValue({
          fields: allFields,
          uid: "interdisciplinaryCriteriaMYP",
        });

        if (
          !_.isEmpty(objectivesMYP) ||
          !_.isEmpty(interdisciplinaryCriteriaMYP)
        ) {
          const convertedHtml = convertMarkdownToHTML({ content });

          const parser = new DOMParser();
          const doc = parser.parseFromString(convertedHtml, "text/html");

          const interdisciplinaryCriteriaMYPTableData = getTableDataForMYPRubric(
            {
              doc,
              assessmentDetails,
              type: MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
              metadata,
            }
          );

          const objectivesMYPTableData = getTableDataForMYPRubric({
            doc,
            assessmentDetails,
            type: MYP_OBJECTIVE_RUBRIC,
            metadata,
          });

          const tableData = _.concat(
            objectivesMYPTableData,
            interdisciplinaryCriteriaMYPTableData
          );

          mypCriteriaMapping = _.merge({}, ..._.map(tableData, "mapping"));
        }

        let isDraftTaskMessage = false;
        const assistantMessageExtraParams = {};

        if (isAITutor) {
          if (getIsAITutorEnhancementsV2Enabled()) {
            const result = await processToolResponse({
              parsedMessages,
              assessmentDetails,
            });

            isDraftTaskMessage = result.isDraftTaskMessage;

            if (!_.isEmpty(result.attachments)) {
              assistantMessageExtraParams.attachments = result.attachments;
            }

            // Clear learning tool selection if the intended tool is called and modality has been created.
            const isModalityCreated = checkIfIntendedModalityCreated({
              parsedMessages,
              learningTool,
            });

            if (isModalityCreated) {
              setSelectedLearningTool?.(null);
            }
          } else if (!_.isEmpty(response)) {
            // get title, description, learning intentions from the AI content by using regex
            const assessmentFieldValues = await getFormattedAssessmentFields({
              inputString: content,
              captureAndUploadImage,
              language: languageCode || language,
              isAITutor,
              fieldList,
            });

            if (!_.isEmpty(assessmentFieldValues)) {
              isDraftTaskMessage = true;

              // parse the data (trim, remove extra spaces, filter empty data etc.)
              const params = await getParamsForAddToLEAction({
                assessmentFieldValues,
                fieldList,
              });

              // update assessment details
              dispatch(
                updateAssignmentField({
                  t,
                  key: "add_to_le",
                  assessmentDetails,
                  params,
                  field_list: fieldList,
                  portalType: "PLANNER",
                  content,
                  isAITutor,
                })
              );
            }

            const assessmentToolTypes = getToolTypeFromContent({
              language: languageCode || language,
              content,
              metadata,
              assessmentDetails,
            });

            dispatch(
              updateAssignmentField({
                t,
                key: "add_assessment_tool",
                assessmentDetails,
                params,
                field_list: fieldList,
                portalType: "PLANNER",
                content,
                selectedAssessmentField: _.first(assessmentToolTypes),
                metadata,
                isAITutor,
              })
            );

            if (_.has(assessmentFieldValues, "tutorLearningGoals")) {
              dispatch(
                updateAIConversation({
                  input: {
                    id: aiConversationId,
                    metadata: {
                      ..._.get(conversation, "metadata", {}),
                      tutorLearningGoals: _.get(
                        assessmentFieldValues,
                        "tutorLearningGoals"
                      ),
                    },
                  },
                })
              );
            }
          }
        } else {
          if (getIsLessonPlanningAIRevampEnabled()) {
            await processToolResponse({
              parsedMessages,
              assessmentDetails,
            });
          }
        }

        // Push the assistant message to messagesInput for bulk mutation
        messagesInput.push({
          authorType: "assistant",
          content,
          usageMetricsId,
          attachments: null,
          metadata: {
            fieldType,
            userInputFlagged,
            model,
            temperature,
            intentUsageMetricsId,
            metadata,
            language: languageCode || language,
            mypCriteriaMapping,
            isDraftTaskMessage,
            ..._.pick(promptMetadata, [
              "options",
              "showActions",
              "selectedOption",
            ]),
            isInterrupted: !_.isEmpty(responseContent) && _.isEmpty(response),
            ...(assistantMetadata || {}),
          },
          parentMessageId,
          ...assistantMessageExtraParams,
        });

        let openModalityId = null;
        // 8. Call bulk mutation to save all messages
        if (!_.isEmpty(messagesInput)) {
          const response = await dispatch(
            createBulkAiMessages({
              conversationId: aiConversationId,
              messages: messagesInput,
            })
          );

          const lastAssistantMessage = _.findLast(
            response,
            ({ attachments, authorType }) => {
              return authorType === "assistant" && !_.isEmpty(attachments);
            }
          );

          const lastAssistantMessageAttachmentId = _.get(
            lastAssistantMessage,
            "attachments[0].id",
            null
          );

          openModalityId = lastAssistantMessageAttachmentId;

          const aiConversation = getAIConversationsFromCache({
            id: aiConversationId,
          });

          const updatedData = update(aiConversation, {
            metadata: {
              $merge: {
                ..._.get(aiConversation, "metadata", {}),
                promptFields,
              },
            },
          });

          writeAIConversationsInCache({
            variables: {
              id: aiConversationId,
            },
            data: {
              node: updatedData,
            },
          });
        }

        if (isAITutor) {
          if (isRevampUIEnabled) {
            dispatch(updateAIDataInRedux({ openModalityId }));
          } else {
            dispatch(
              updateAIDataInRedux({
                shouldGeneratePreviewChat: true,
                isAITutorUpdated: mode === "edit",
              })
            );
          }
        }
      }
    } catch (e) {
      setCurrentAIState("");
      setSendButtonParams({});
    }
  };
};

export const createAIConversation = ({ input = {} }) => {
  return async (_dispatch, getState) => {
    try {
      const {
        entityId,
        entityType = null,
        entitySubType = "TASK_CREATION",
      } = input;

      const response = await client.mutate({
        mutation: createAIConversationMutation,
        variables: { input },
        update: (
          cache,
          {
            data: {
              ai: { createAiConversation },
            },
          }
        ) => {
          const userAcademicYearId = getSelectedAcademicYearId(getState());
          const collaboratorsFilter = { userAcademicYearId };

          const conversationId = _.get(createAiConversation, "response.id");

          cache.updateQuery(
            {
              query: getAssessmentQuery,
              variables: {
                id: entityId,
                collaboratorsFilter,
              },
            },
            data => {
              if (!data) return data;

              return update(data, {
                node: {
                  aiConversation: {
                    edges: {
                      $set: [
                        ..._.get(data, "node.aiConversation.edges", []),
                        {
                          node: {
                            id: conversationId,
                            entityType,
                            entitySubType,
                            metadata: null,
                            attachments: [],
                            analysisToolEvaluation: [],
                            messages: [],
                            __typename: "AiConversation",
                          },
                          __typename: "AiConversationEdge",
                        },
                      ],
                    },
                    __typename: { $set: "AiConversationConnection" },
                  },
                },
              });
            }
          );
        },
      });

      return _.get(response, "data.ai.createAiConversation", {});
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const updateUnitPlanField = ({
  t,
  params,
  unitPlanFields = [],
  extraParams = {},
  field_list = {},
  isMutation = true,
  hasAddRemoveValues = false,
  isDebounce = true,
  unitPlanId,
}) => {
  return async (dispatch, getState) => {
    _.map(params, async (obj, key) => {
      const fieldObj = _.find(unitPlanFields, { uid: key }) || {};

      if (_.isEmpty(fieldObj)) return;

      const { id, value: oldValue } = fieldObj;
      const { valuesToAdd, valuesToRemove } = extraParams || {};

      const value = {
        id,
        value: obj,
      };

      // if same value is being set then no need to update the cache and make a call to the backend
      if (_.isEqual(obj, oldValue)) return;

      dispatch(
        updateUnitPlanInCache({
          fieldUID: key,
          params: value,
          fieldObj,
          extraParams,
          fieldTemplateObj: field_list[key],
        })
      );

      if (isMutation) {
        dispatch(
          updateField({
            key: id,
            params: {
              unitPlanId,
              ...getPlannerFieldMutationParams({
                id,
                oldValue,
                hasAddRemoveValues,
                valuesToAdd,
                valuesToRemove,
                newValue: _.get(value, "value", []),
                fieldUID: key,
              }),
            },
            type: ELEMENT_TYPE_UNIT_PLAN,
            isDebounce,
            t,
          })
        );
      }
    });
  };
};

export const captureAndUploadImage = element => {
  return async (dispatch, getState) => {
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        logging: false,
        scale: 1,
      });

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(resolve, "image/png", 1);
      });

      if (blob) {
        const randomNumber = generateRandomNumber(0, 6);
        const url = await dispatch(
          uploadFileV2({
            file: blob,
            status: `COPYINGIMAGE`,
            attachment: {
              mimeType: blob.type,
              metadata: {
                fileExtension: blob.type.split("/")[1],
                fileGroup: "content/rt/uploads",
              },
              type: `IMAGE_${randomNumber}`,
            },
            uploadId: generateRandomId(),
          })
        );

        return url;
      }

      return null;
    } catch (err) {
      return null;
    }
  };
};

export const updateAIMessage = variables => {
  return async dispatch => {
    try {
      return await client.mutate({
        mutation: updateAIMessageMutation,
        variables,
      });
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
